import useFormat from "hooks/useFormat";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { useColorScaleVDP, VerticalDotPlotAuto } from "../charts-motion";
import { withFigureIO } from "../components/figure";
import { metadata } from "../data/data_quality_fig_BULLY_FIGHT";
import { datasets } from "../data/data_quality_fig_BULLY_FIGHT_datasets";
import { Gender, getGenderName, mkGemEntityDecoder, ordGender, useButtonGroupState, useCountryEntities, useFigureControlItems, useFigureState, useNamedEntities } from "../domain";
import { useMedia } from "../hooks";
import { unsafeFromArray } from "../lib";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { Ar, identity, io, Ord } from "../prelude";
export * from "../data/data_quality_fig_BULLY_FIGHT";
const DEFAULT_INDICATOR = "PER.11T15.BULLIED";
const Indicator = io.keyof({
    "PER.11T15.BULLIED": null,
    "SCHL.VIOL": null
});
const indicatorLookup = {
    "PER.11T15.BULLIED": defineMessage({
        id: "fig.quality.BULLY_FIGHT.PER.11T15.BULLIED",
        defaultMessage: "Experienced bullying"
    }),
    "SCHL.VIOL": defineMessage({
        id: "fig.quality.BULLY_FIGHT.SCHL.VIOL",
        defaultMessage: "Involved in physical fights"
    })
};
export const Data = mkGemEntityDecoder([], {
    value: io.number,
    ind_id: Indicator,
    gender: Gender
});
export const Chart = ({ data: dataRaw })=>{
    const intl = useIntl();
    const screenMDown = !useMedia(breakpoints.m);
    const [, actions] = useFigureState();
    const [selectedIndicator] = useButtonGroupState("indicator");
    const entityData_ = useNamedEntities(dataRaw);
    const entityData = React.useMemo(()=>{
        const data = entityData_.map((x)=>({
                ...x,
                gender_name: getGenderName(intl, x.gender)
            }));
        return Ar.sortBy([
            Ord.contramap((x)=>x.ind_id)(Ord.ordString),
            Ord.contramap((x)=>x.gender)(ordGender),
            Ord.contramap((x)=>x.value)(Ord.ordNumber)
        ])(data);
    }, [
        intl,
        entityData_
    ]);
    const countryData = useCountryEntities(entityData);
    const indicatorControlItems = useFigureControlItems(entityData, (x)=>x.ind_id, (x)=>intl.formatMessage(indicatorLookup[x.ind_id]));
    React.useEffect(()=>{
        actions.setSelectionControl("indicator", {
            type: "ButtonGroup",
            selected: DEFAULT_INDICATOR
        }, indicatorControlItems);
    }, [
        actions,
        indicatorControlItems
    ]);
    const chartData = React.useMemo(()=>{
        return unsafeFromArray(countryData.filter((x)=>x.ind_id === selectedIndicator));
    }, [
        countryData,
        selectedIndicator
    ]);
    const getX = React.useCallback((x)=>x.entity_name, []);
    const getValue = React.useCallback((x)=>x.value, []);
    const getColor = React.useCallback((x)=>x.gender_name, []);
    const getColorPalette = React.useCallback(()=>M.colorRanges.discrete, []);
    const formatY = useFormat(".0%");
    const [colorScale, colorLegendValues] = useColorScaleVDP({
        data: chartData,
        getColor,
        colorPalette: getColorPalette,
        formatColor: identity
    });
    return <VerticalDotPlotAuto height={screenMDown ? M.chartHeight.s : M.chartHeight.m} data={chartData} getX={getX} getValue={getValue} getColor={getColor} formatX={identity} formatValue={formatY} domainY={[
        0,
        1
    ]} colorScale={colorScale} colorLegendValues={colorLegendValues} showTooltip showLegend dotStyle={"small"} labelStyleX="none"/>;
};
export default withFigureIO({
    url: new URL("../data/data_quality_fig_BULLY_FIGHT.json", import.meta.url).href,
    csv: new URL("../data/data_quality_fig_BULLY_FIGHT.zip", import.meta.url).href,
    xlsx: new URL("../data/data_quality_fig_BULLY_FIGHT.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    datasets
});
